/**
 * Authentication token provider that works **outside the React tree**.
 *
 * React Router loaders execute before the route component mounts, so they
 * cannot use React hooks like `useAuth()`.  This module provides a
 * standalone `getAuthToken()` that reads the Clerk session token via
 * `window.Clerk` (official `@clerk/shared/getToken` API), or falls back to
 * the localStorage bootstrap admin token when Clerk is not configured.
 *
 * @module auth-token
 */

/* ── Clerk token (works outside React) ──────────────────────── */

const clerkPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;
const clerkJwtTemplate = import.meta.env.VITE_CLERK_JWT_TEMPLATE as string | undefined;

/**
 * Get the Clerk session token using the **framework-agnostic** API.
 *
 * This uses `@clerk/shared/getToken` which reads `window.Clerk` directly,
 * waiting for Clerk to initialize if necessary.  It is safe to call from
 * React Router loaders, Axios interceptors, or any vanilla JS code.
 */
async function getClerkToken(): Promise<string | null> {
  // Dynamic import so the module is only loaded when Clerk is enabled.
  const { getToken } = await import('@clerk/shared/getToken');
  if (clerkJwtTemplate) {
    return (await getToken({ template: clerkJwtTemplate })) ?? (await getToken());
  }
  return getToken();
}

/* ── Admin-token fallback (localStorage) ────────────────────── */

function getAdminToken(): string | null {
  try {
    return localStorage.getItem('apx.adminToken') || null;
  } catch {
    return null;
  }
}

/* ── Public helper ───────────────────────────────────────────── */

/**
 * Get an auth token that works in both Clerk and admin-token mode.
 *
 * - **Clerk mode**: Reads the session JWT via `window.Clerk`.  If Clerk
 *   hasn't loaded yet, the promise will wait up to 10 seconds.
 * - **Admin-token mode**: Reads from `localStorage('apx.adminToken')`.
 * - Returns `null` when no token is available (e.g., not signed in).
 *
 * Safe to call from React Router loaders, SWR fetchers, or any
 * non-React context.
 */
export async function getAuthToken(): Promise<string | null> {
  if (clerkPublishableKey) {
    return getClerkToken();
  }
  return getAdminToken();
}
